import React, { useCallback, useEffect, useMemo, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import {
  ChamberMemberReturn,
  GetAllChamberMembersQuery,
  ShopLocalCampaignParticipatingMembersInput,
} from "../../../graphql/types";
import { makeStyles, FormHelperText } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import Loader from "../../common/Loader/Loader";
import { FormikErrors, FormikTouched } from "formik";
import { useEnumStrings } from "../../enumStrings";

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    fontFamily: "DM Sans, sans-serif",
  },
  searchBox: {
    position: "relative" as const,
    width: "100%",
    marginTop: "8px",
  },
  searchIcon: {
    position: "absolute" as const,
    top: "50%",
    left: "14px",
    transform: "translateY(-50%)",
    color: "#9E9E9E",
    fontSize: "20px",
  },
  searchInput: {
    width: "100%",
    height: "44px",
    padding: "0 14px 0 42px",
    fontSize: "15px",
    fontFamily: "DM Sans, sans-serif",
    border: "1px solid #E0E0E0",
    borderRadius: "10px",
    outline: "none",
    background: "#FFFFFF",
    boxSizing: "border-box" as const,
    "&:focus": {
      borderColor: "#E1A731",
    },
  },
  counterRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "14px 0 10px",
    fontSize: "14px",
    color: "#666",
  },
  countNumber: {
    color: "#E1A731",
    fontWeight: 700,
  },
  clearBtn: {
    background: "none",
    border: "none",
    color: "#E1A731",
    fontWeight: 600,
    fontSize: "14px",
    cursor: "pointer",
    padding: 0,
    fontFamily: "DM Sans, sans-serif",
    "&:hover": { textDecoration: "underline" },
  },
  listContainer: {
    border: "1px solid #E0E0E0",
    borderRadius: "10px",
    overflow: "hidden",
    background: "#FFFFFF",
  },
  selectAllRow: {
    display: "flex",
    alignItems: "center",
    padding: "10px 14px",
    background: "#F5F6F7",
    borderBottom: "1px solid #E0E0E0",
    fontFamily: "DM Sans, sans-serif",
  },
  selectAllLabel: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#1F2937",
    marginLeft: "4px",
    flex: 1,
  },
  totalCount: {
    fontSize: "13px",
    color: "#9CA3AF",
  },
  sectionHeader: {
    padding: "8px 14px",
    background: "#F5F6F7",
    borderBottom: "1px solid #E0E0E0",
    borderTop: "1px solid #E0E0E0",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.05em",
    color: "#6B7280",
    fontFamily: "DM Sans, sans-serif",
  },
  selectedItem: {
    display: "flex",
    alignItems: "center",
    padding: "10px 14px",
    background: "#FFFFFF",
    borderLeft: "3px solid #E1A731",
    borderBottom: "1px solid #F0F0F0",
    cursor: "pointer",
    fontFamily: "DM Sans, sans-serif",
    "&:hover": { background: "#FAFAFA" },
    "&:last-child": { borderBottom: "none" },
  },
  memberItem: {
    display: "flex",
    alignItems: "center",
    padding: "10px 14px",
    borderBottom: "1px solid #F0F0F0",
    cursor: "pointer",
    fontFamily: "DM Sans, sans-serif",
    "&:hover": { background: "#FAFAFA" },
    "&:last-child": { borderBottom: "none" },
  },
  itemLabel: {
    fontSize: "15px",
    color: "#1F2937",
    fontWeight: 600,
    marginLeft: "4px",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "28px 16px",
    gap: "4px",
    background: "#FFFFFF",
  },
  emptyTitle: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#1F2937",
  },
  emptyHint: {
    fontSize: "13px",
    color: "#9CA3AF",
  },
  emptyIcon: {
    fontSize: "24px",
    color: "#9CA3AF",
    marginBottom: "4px",
  },
  checkbox: {
    padding: "4px",
    color: "#D1D5DB !important",
    "&.Mui-checked": { color: "#E1A731 !important" },
    "&.MuiCheckbox-indeterminate": { color: "#E1A731 !important" },
  },
  scrollList: {
    maxHeight: "320px",
    overflowY: "auto" as const,
  },
}));

export default function ChooseParticipatingMembers({
  selectedChamberMembers,
  handleChamberMembersChange,
  allChamberMemberData,
  formikTouchedAvailableToChamberIds,
  formikErrorsAvailableToChamberIds,
}: {
  selectedChamberMembers: ShopLocalCampaignParticipatingMembersInput[];
  handleChamberMembersChange: (
    e: ShopLocalCampaignParticipatingMembersInput[]
  ) => void;
  allChamberMemberData: GetAllChamberMembersQuery | undefined;
  formikTouchedAvailableToChamberIds:
    | FormikTouched<ShopLocalCampaignParticipatingMembersInput>[]
    | undefined;
  formikErrorsAvailableToChamberIds:
    | string
    | string[]
    | FormikErrors<ShopLocalCampaignParticipatingMembersInput>[]
    | undefined;
}) {
  const classes = useStyles();
  const [checked, setChecked] = useState<
    ShopLocalCampaignParticipatingMembersInput[]
  >([]);
  const [fullList, setFullList] = useState<
    Pick<ChamberMemberReturn, "id" | "name">[]
  >([]);

  const handleToggle = useCallback(
    (
      newCheckState: boolean,
      value: ShopLocalCampaignParticipatingMembersInput
    ) =>
      () => {
        const newChecked = [...checked];

        if (newCheckState) {
          newChecked.push(value);
        } else {
          const currentIndex = checked.findIndex((obj) => obj.id === value.id);
          newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
        handleChamberMembersChange(newChecked);
      },
    [checked, handleChamberMembersChange]
  );

  const handleToggleAll = useCallback(
    (items: ShopLocalCampaignParticipatingMembersInput[]) => () => {
      if (checked.length === items.length) {
        setChecked([]);
        handleChamberMembersChange([]);
      } else {
        setChecked(fullList);
        handleChamberMembersChange(fullList);
      }
    },
    [checked.length, fullList, handleChamberMembersChange]
  );

  const [isLoading, setIsLoading] = useState(true);

  const [searchInput, setSearchInput] = useState("");
  const handleSearchChange = useCallback((e: any) => {
    e.preventDefault();
    setSearchInput(e.target.value);
  }, []);

  const selectedChamberMemberIds = useMemo(() => {
    return selectedChamberMembers.map((chamberMember) => chamberMember.id);
  }, [selectedChamberMembers]);

  useEffect(() => {
    if (isLoading) {
      const allChamberMemberSelectableArray = (
        allChamberMemberData?.getAllChamberMembers || []
      )
        .map(({ id, name }) => ({
          id,
          name,
        }))
        .sort(function (a, b) {
          return a.name.localeCompare(b.name);
        });

      if (selectedChamberMembers && selectedChamberMembers.length > 0) {
        setChecked(
          (allChamberMemberSelectableArray || []).filter((chamberMember) =>
            selectedChamberMemberIds.includes(chamberMember.id)
          ) || []
        );
      } else {
        setChecked([]);
      }

      setFullList(allChamberMemberSelectableArray || []);
      setIsLoading(false);
    }
  }, [
    allChamberMemberData?.getAllChamberMembers,
    selectedChamberMembers,
    isLoading,
    selectedChamberMemberIds,
  ]);

  const enumStrings = useEnumStrings();

  const searchedItems = useMemo(() => {
    return fullList.filter((chamberMember) => {
      return chamberMember.name.toLowerCase().match(searchInput.toLowerCase());
    });
  }, [fullList, searchInput]);

  const availableItems = useMemo(
    () =>
      searchedItems.filter(
        (item) => !selectedChamberMemberIds.includes(item.id)
      ),
    [searchedItems, selectedChamberMemberIds]
  );

  const visibleSelectedItems = useMemo(
    () =>
      checked.filter((item) =>
        item.name.toLowerCase().includes(searchInput.toLowerCase())
      ),
    [checked, searchInput]
  );

  const handleClearAll = useCallback(() => {
    setChecked([]);
    handleChamberMembersChange([]);
  }, [handleChamberMembersChange]);

  const allSelected =
    fullList.length > 0 && checked.length === fullList.length;
  const someSelected = checked.length > 0 && !allSelected;

  if (!allChamberMemberData?.getAllChamberMembers || isLoading) {
    return (
      <div style={{ marginTop: "46px" }}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.searchBox}>
        <SearchIcon className={classes.searchIcon} />
        <input
          type="text"
          className={classes.searchInput}
          placeholder="Search by member company name"
          value={searchInput}
          onChange={handleSearchChange}
        />
      </div>

      <div className={classes.counterRow}>
        <span>
          <span className={classes.countNumber}>{checked.length}</span> of{" "}
          {fullList.length} selected
        </span>
        {checked.length > 0 && (
          <button
            type="button"
            className={classes.clearBtn}
            onClick={handleClearAll}
          >
            Clear
          </button>
        )}
      </div>

      <div className={classes.listContainer}>
        <div
          className={classes.selectAllRow}
          onClick={handleToggleAll(fullList)}
          style={{ cursor: "pointer" }}
        >
          <Checkbox
            className={classes.checkbox}
            checked={allSelected}
            indeterminate={someSelected}
            disabled={fullList.length === 0}
            disableRipple
          />
          <span className={classes.selectAllLabel}>Select all</span>
          <span className={classes.totalCount}>
            {fullList.length} companies
          </span>
        </div>

        <div className={classes.sectionHeader}>SELECTED MEMBERS</div>
        {visibleSelectedItems.length === 0 ? (
          <div className={classes.emptyState}>
            <div className={classes.emptyTitle}>
              {checked.length === 0
                ? "No members selected yet"
                : "No members found"}
            </div>
            {checked.length === 0 && (
              <div className={classes.emptyHint}>
                Check a box below to add a member to this contest
              </div>
            )}
          </div>
        ) : (
          <div className={classes.scrollList}>
            {visibleSelectedItems.map((value) => (
              <div
                key={`selected-${value.id}`}
                className={classes.selectedItem}
                onClick={handleToggle(false, value)}
              >
                <Checkbox
                  className={classes.checkbox}
                  checked
                  disableRipple
                />
                <span className={classes.itemLabel}>{value.name}</span>
              </div>
            ))}
          </div>
        )}

        <div className={classes.sectionHeader}>ALL MEMBERS</div>
        <div className={classes.scrollList}>
          {availableItems.length === 0 ? (
            <div className={classes.emptyState}>
              <div className={classes.emptyTitle}>No members found</div>
            </div>
          ) : (
            availableItems.map((value) => (
              <div
                key={`available-${value.id}`}
                className={classes.memberItem}
                onClick={handleToggle(true, value)}
              >
                <Checkbox
                  className={classes.checkbox}
                  checked={false}
                  disableRipple
                />
                <span className={classes.itemLabel}>{value.name}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <FormHelperText
        component="legend"
        style={{
          color: "#f44336",
          paddingTop: "8px",
          textAlign: "left",
        }}
      >
        {typeof formikErrorsAvailableToChamberIds === "string"
          ? formikErrorsAvailableToChamberIds
          : null}
      </FormHelperText>
    </div>
  );
}

export const ParticipationCodeExplanationText = () => {
  const enumStrings = useEnumStrings();
  return (
    <h4>
      {enumStrings.contestParticipationCode.description}
      <br />
      <br />
      {enumStrings.contestParticipationCode.instructions.Instruction1}
      <br />
      <br />
      {enumStrings.contestParticipationCode.instructions.Instruction2}
      <br />
      <br />
      {enumStrings.contestParticipationCode.instructions.Instruction3}
      <br />
      <br />
      {enumStrings.contestParticipationCode.instructions.Instruction4}
    </h4>
  );
};
